import React from 'react';

import { ModelNode, SourceNode, TestNode } from '@/__generated__/graphql';
import {
  JobBasedExposure,
  useJobBasedExposureQuery,
} from '@/hooks/discovery/useJobBasedExposureQuery';
import { TileStatus } from '../EnvBased/DataMonitor';
import JobBasedDataMonitorFooter from './JobBasedDataMonitorFooter';
import { JobBasedSpinner } from './JobBasedSpinner';
import JobBasedStatusInformation from './JobBasedStatusInformation';
import LastCheckedTime from './LastCheckedTime';

export enum StateStatus {
  Pass = 'PASS',
  Warn = 'WARN',
  Error = 'ERROR',
  Fail = 'FAIL',
}

export const getStateCount = (nodes: any, state: StateStatus): number => {
  return nodes?.filter(
    (node: any) => node?.state?.toUpperCase() === state.toUpperCase(),
  ).length;
};

const getSourcesStatus = (sources: SourceNode[]): TileStatus => {
  const statuses = sources.map((source) =>
    source?.state ? source.state.toUpperCase() : StateStatus.Pass,
  );
  if (statuses.includes(StateStatus.Error.toUpperCase())) {
    return TileStatus.error;
  }
  return TileStatus.pass;
};

const getTestsStatus = (tests: TestNode[]): TileStatus => {
  const statuses = tests.map((test) =>
    test?.state ? test.state.toUpperCase() : StateStatus.Pass,
  );

  if (
    statuses.includes(StateStatus.Error.toUpperCase()) ||
    statuses.includes(StateStatus.Fail.toUpperCase())
  ) {
    return TileStatus.error;
  }
  return TileStatus.pass;
};

const getTests = (data?: JobBasedExposure): TestNode[] => {
  const models: ModelNode[] = (data?.job?.exposure?.parentsModels as ModelNode[]) || [];
  const sources: SourceNode[] =
    (data?.job?.exposure?.parentsSources as SourceNode[]) || [];

  const modelsTests = models
    .map((model) => {
      return (model?.tests as TestNode[]) || [];
    })
    .flat();

  const sourcesTests = sources
    .map((source) => {
      return (source?.tests as TestNode[]) || [];
    })
    .flat();

  return [...modelsTests, ...sourcesTests];
};

const determineDataFreshnessTitle = (tileStatus: TileStatus): string => {
  const title = {
    [TileStatus.pass]: 'Data freshness passed.',
    [TileStatus.error]: 'Data may be stale.',
    [TileStatus.unknown]: 'Data may be stale.',
    [TileStatus.warn]: 'Data may be stale.',
    [TileStatus.skipped]: 'Data freshness skipped.',
  };
  return title[tileStatus];
};

const determineDataQualityTitle = (tileStatus: TileStatus): string => {
  const title = {
    [TileStatus.pass]: 'Data quality passed.',
    [TileStatus.error]: 'Data quality degraded.',
    [TileStatus.unknown]: 'Data quality degraded.',
    [TileStatus.warn]: 'Data quality degraded.',
    [TileStatus.skipped]: 'Data quality skipped.',
  };
  return title[tileStatus];
};

const determineDataFreshnessSubtitle = (data?: JobBasedExposure): string => {
  const sources: SourceNode[] =
    (data?.job?.exposure?.parentsSources as SourceNode[]) || [];

  const sourcesSuccessCount = getStateCount(sources, StateStatus.Pass);
  const sourcesWarnCount = getStateCount(sources, StateStatus.Warn);
  const sourcesFailCount = getStateCount(sources, StateStatus.Error);

  return `Sources:
    ${sourcesSuccessCount} fresh,
    ${sourcesWarnCount} delayed,
    ${sourcesFailCount} stale`;
};

const determineDataQualitySubtitle = (data?: JobBasedExposure): string => {
  const tests = getTests(data);
  const testsSuccessCount = getStateCount(tests, StateStatus.Pass);
  const testsWarnCount = getStateCount(tests, StateStatus.Warn);
  const testsErrorCount =
    getStateCount(tests, StateStatus.Error) + getStateCount(tests, StateStatus.Fail);

  return `Tests:
    ${testsSuccessCount} passed,
    ${testsWarnCount} ${testsWarnCount === 1 ? `warning` : `warnings`},
    ${testsErrorCount} ${testsErrorCount === 1 ? `error` : `errors`}`;
};

const displayDataFreshnessSubtitle = (data?: JobBasedExposure): boolean => {
  const sources: SourceNode[] =
    (data?.job?.exposure?.parentsSources as SourceNode[]) || [];

  const sourcesWarnCount = getStateCount(sources, StateStatus.Warn);
  const sourcesFailCount = getStateCount(sources, StateStatus.Error);

  return sourcesWarnCount >= 1 || sourcesFailCount >= 1;
};

const displayDataQualitySubtitle = (data?: JobBasedExposure): boolean => {
  const tests = getTests(data);
  const testsWarnCount = getStateCount(tests, StateStatus.Warn);
  const testsErrorCount =
    getStateCount(tests, StateStatus.Error) + getStateCount(tests, StateStatus.Fail);

  return testsWarnCount >= 1 || testsErrorCount >= 1;
};

type JobBasedDataMonitorProps = {
  jobId: number;
  exposureName: string;
};

export const JobBasedDataMonitor: React.FC<JobBasedDataMonitorProps> = ({
  jobId,
  exposureName,
}) => {
  const determineFreshnessStatus = (data: JobBasedExposure | undefined): TileStatus => {
    const sources: SourceNode[] =
      (data?.job?.exposure?.parentsSources as SourceNode[]) || [];

    return getSourcesStatus([...sources]);
  };

  const determineQualityStatus = (data: JobBasedExposure | undefined): TileStatus => {
    const models: ModelNode[] =
      (data?.job?.exposure?.parentsModels as ModelNode[]) || [];
    const sources: SourceNode[] =
      (data?.job?.exposure?.parentsSources as SourceNode[]) || [];

    const modelsTests = models
      .map((model) => {
        return (model?.tests as TestNode[]) || [];
      })
      .flat();

    const sourcesTests = sources
      .map((source) => {
        return (source?.tests as TestNode[]) || [];
      })
      .flat();

    return getTestsStatus([...modelsTests, ...sourcesTests]);
  };

  const [{ data, fetching, error }] = useJobBasedExposureQuery({
    jobId,
    name: exposureName,
  });

  const freshnessStatus = determineFreshnessStatus(data);
  const qualityStatus = determineQualityStatus(data);

  const freshnessTitle = determineDataFreshnessTitle(freshnessStatus);
  const qualityTitle = determineDataQualityTitle(qualityStatus);
  const freshnessSubtitle = determineDataFreshnessSubtitle(data);
  const qualitySubtitle = determineDataQualitySubtitle(data);
  const displayFreshnessSubtitle = displayDataFreshnessSubtitle(data);
  const displayQualitySubtitle = displayDataQualitySubtitle(data);

  return (
    <>
      {fetching ? (
        <div className="flex justify-center py-20">
          <JobBasedSpinner />
        </div>
      ) : error ? (
        <div className="flex justify-center py-20">
          <p className="mx-12 text-center">{error.message}</p>
        </div>
      ) : data?.job?.exposure?.parentsSources &&
        data.job.exposure.parentsSources.length > 0 ? (
        <>
          <JobBasedStatusInformation
            dataStatus={freshnessStatus}
            title={freshnessTitle}
            subtitle={freshnessSubtitle}
            displaySubtitle={displayFreshnessSubtitle}
          />
          <JobBasedStatusInformation
            dataStatus={qualityStatus}
            title={qualityTitle}
            subtitle={qualitySubtitle}
            displaySubtitle={displayQualitySubtitle}
          />
          <LastCheckedTime sources={data?.job?.exposure?.parentsSources} />
        </>
      ) : (
        <div className="flex justify-center py-20">
          <p className="mx-12 text-center">
            Configure{' '}
            <a
              target="_blank"
              rel="noopener noreferrer"
              className="text-dbt-link-blue"
              href="https://docs.getdbt.com/docs/dbt-cloud/using-dbt-cloud/cloud-snapshotting-source-freshness/#enabling-source-freshness-snapshots"
            >
              source snapshot freshness
            </a>{' '}
            to view the health of your data.
          </p>
        </div>
      )}
      <div className="px-8">
        <hr className="text-dbt-light-gray border" />
      </div>
      <JobBasedDataMonitorFooter
        accountId={data?.job?.exposure?.accountId}
        projectId={data?.job?.exposure?.projectId}
        jobId={data?.job?.exposure?.jobId}
        exposureName={exposureName}
      />
    </>
  );
};
